import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import kwameImage from "../assets/kwame-sarfo.jpg"; // ✅ Make sure this file exists

const About = () => {
  const title = "About Kwame O. Sarfo | Ghanaian Entrepreneur & Businessman";
  const description =
    "Learn about Kwame O. Sarfo — a Ghanaian entrepreneur, founder of Solonet Properties, Ice Chest Mineral Water, and Global Timber Ltd. Discover his early life, business ventures, and philanthropic works in education, healthcare, and community development.";
  const keywords =
    "Kwame O. Sarfo, Kwame Sarfo, Solonet Properties owner, Ghana businessman, Ghana entrepreneur, Ice Chest Water, Global Timber Ltd, real estate Ghana, philanthropy Ghana, private sector, leadership, investment";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Kwame O. Sarfo",
    jobTitle: "Entrepreneur, Investor, Philanthropist",
    nationality: "Ghanaian",
    image: "https://solonetproperties.com/assets/kwame-sarfo.jpg",
    worksFor: [
      {
        "@type": "Organization",
        name: "Solonet Properties",
        url: "https://solonetproperties.com",
      },
      {
        "@type": "Organization",
        name: "Ice Chest Mineral Water",
        url: "https://icechestwater.com",
      },
      {
        "@type": "Organization",
        name: "Global Timber Ltd",
      },
    ],
    description: description,
    sameAs: [
      "https://solonetproperties.com",
      "https://icechestwater.com",
    ],
    url: "https://solonetproperties.com/about",
  };

  return (
    <main
      id="about"
      className="relative bg-[#0a0a0a] text-white py-20 px-6 md:px-20 overflow-hidden"
    >
      {/* SEO META TAGS */}
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content="https://solonetproperties.com/assets/kwame-sarfo.jpg"
        />
        <meta property="og:url" content="https://solonetproperties.com/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta
          name="twitter:image"
          content="https://solonetproperties.com/assets/kwame-sarfo.jpg"
        />
        <link rel="canonical" href="https://solonetproperties.com/about" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Header Section with Image */}
      <header className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 text-center md:text-left mb-20">
        {/* Profile Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="flex-shrink-0"
        >
          <img
            src={kwameImage}
            alt="Kwame O. Sarfo portrait"
            width={400}
            height={400}
            loading="lazy"
            className="rounded-2xl shadow-lg border-2 border-[#d4af37]/40 object-cover w-64 h-64 md:w-80 md:h-80"
          />
        </motion.div>

        {/* Text Content */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="max-w-xl"
        >
          <h1 className="text-5xl md:text-6xl font-playfair text-[#d4af37]">
            Kwame O. Sarfo
          </h1>
          <p className="mt-3 text-gray-400 text-lg">
            Entrepreneur • Visionary • Philanthropist
          </p>
          <p className="mt-6 text-gray-300 leading-relaxed">
            Kwame O. Sarfo is a Ghanaian entrepreneur and businessman best known
            as the founder of{" "}
            <span className="text-[#d4af37]">Solonet Properties</span>,{" "}
            <span className="text-[#d4af37]">Ice Chest Mineral Water</span>, and{" "}
            <span className="text-[#d4af37]">Global Timber Ltd</span>. His
            ventures span real estate, water production, timber export, and
            strategic investments in energy and community development.
          </p>
        </motion.div>
      </header>

      {/* Biography Section */}
      <motion.article
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="max-w-5xl mx-auto text-gray-300 leading-relaxed space-y-10"
      >
        {/* Early Life */}
        <section aria-labelledby="education">
          <h2
            id="education"
            className="text-2xl font-playfair text-[#d4af37] mb-3"
          >
            Early Life & Education
          </h2>
          <p>
            Kwame attended{" "}
            <span className="text-[#d4af37]">Achimota School</span> for his
            basic education and completed his senior high at{" "}
            <span className="text-[#d4af37]">
              Pope John Senior High School
            </span>
            . He later earned a Diploma in Accounting from the{" "}
            <span className="text-[#d4af37]">
              University of Professional Studies, Accra (UPSA)
            </span>{" "}
            and pursued banking studies at the{" "}
            <span className="text-[#d4af37]">Ghana Institute of Bankers</span>.
            His entrepreneurial path began when his father, a successful
            businessman in the steel and construction industries, introduced him
            to business management and investment.
          </p>
        </section>

        {/* Career */}
        <section aria-labelledby="career">
          <h2 id="career" className="text-2xl font-playfair text-[#d4af37] mb-3">
            Career
          </h2>
          <p>
            Inspired by his father’s work ethic and vision, Kwame established a
            diverse portfolio of companies, including:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>
              <strong className="text-[#d4af37]">Solonet Properties</strong> — A
              modern real estate firm focused on sustainable and innovative
              housing development.
            </li>
            <li>
              <strong className="text-[#d4af37]">Ice Chest Mineral Water</strong>{" "}
              — A bottled water brand emphasizing purity, freshness, and
              eco-friendly packaging.
            </li>
            <li>
              <strong className="text-[#d4af37]">Global Timber Ltd</strong> — A
              timber export company connecting Ghana’s hardwood to global
              markets.
            </li>
          </ul>
        </section>

        {/* Philanthropy */}
        <section aria-labelledby="philanthropy">
          <h2
            id="philanthropy"
            className="text-2xl font-playfair text-[#d4af37] mb-3"
          >
            Philanthropy
          </h2>
          <p>
            Kwame O. Sarfo’s philanthropy centers on empowering the next
            generation through education, faith, and healthcare. His initiatives
            include:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>Building schools and churches in underserved communities</li>
            <li>Supporting hospitals and community health projects</li>
            <li>
              Funding education and healthcare for underprivileged individuals
            </li>
          </ul>
        </section>

        {/* Footer */}
        <footer className="pt-8 border-t border-[#d4af37]/20">
          <p className="text-sm text-gray-500">
            Learn more:{" "}
            <a
              href="https://solonetproperties.com"
              className="text-[#d4af37] hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Solonet Properties
            </a>{" "}
            |{" "}
            <a
              href="https://icechestmineralwater.com/"
              className="text-[#d4af37] hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Ice Chest Mineral Water
            </a>
          </p>
        </footer>
      </motion.article>
    </main>
  );
};

export default About;
